import * as THREE from 'three'
import type { GraphicsBackend } from '@/graphics/GraphicsBackend'
import { BackendMismatchError } from '@/graphics/GraphicsErrors'
import { assertGraphicsRequirements } from '@/graphics/GraphicsRequirements'
import {
  defineLegacyOutputTargetAccessor,
  defineLegacyRendererAccessor,
} from '@/graphics/legacyGraphicsAccessors'
import type { EventBus } from '@/runtime/EventBus'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type { PhaseRenderContext } from '@/shared/types/assets'
import { RenderPhase } from '@/shared/types/assets'
import type {
  CameraClipBoundsContext,
  RenderPipeline as IRenderPipeline,
  PostProcessPass,
  RenderActivityContext,
  RenderActivityReport,
  RenderContributor,
  RenderInvalidationService,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'

const PHASE_ORDER: readonly RenderPhase[] = [
  RenderPhase.Prepare,
  RenderPhase.MainOpaque,
  RenderPhase.MainTransparent,
  RenderPhase.OcclusionDepth,
  RenderPhase.MainOcclusionTested,
  RenderPhase.Overlay3D,
  RenderPhase.Overlay2D,
  RenderPhase.PostProcess,
]
const RENDER_PHASE_SLOW_MS = 8
const log = getLoggerManager().getLogger('render:pipeline')

/** 读取 backend 在区域采集期间暴露的真实提交目标；未处于采集时为 null。 */
function resolveLegacyCaptureTarget(graphics: GraphicsBackend): THREE.WebGLRenderTarget | null {
  const provider = graphics as GraphicsBackend & {
    getLegacyCaptureTarget?: () => THREE.WebGLRenderTarget | null
  }
  return typeof provider.getLegacyCaptureTarget === 'function'
    ? provider.getLegacyCaptureTarget()
    : null
}

function isFiniteBox(box: THREE.Box3): boolean {
  const { min, max } = box
  return (
    Number.isFinite(min.x) &&
    Number.isFinite(min.y) &&
    Number.isFinite(min.z) &&
    Number.isFinite(max.x) &&
    Number.isFinite(max.y) &&
    Number.isFinite(max.z)
  )
}

/** Options for executing one render pipeline pass. */
export interface RenderPipelineExecuteOptions {
  /** Optional render target used by contributors. */
  outputTarget?: THREE.WebGLRenderTarget | null
  /** Optional legacy WebGL bottom-left viewport that overrides the frame viewport. */
  viewportOverride?: {
    x: number
    y: number
    width: number
    height: number
  }
}

/**
 * 渲染管线，按阶段顺序驱动 RenderContributor 并维持渲染器状态。
 */
export class RenderPipeline implements IRenderPipeline {
  private contributors: Set<RenderContributor> = new Set()
  private postProcessPasses: Set<PostProcessPass> = new Set()
  private readonly eventBus: EventBus
  private disposed = false

  constructor(
    eventBus: EventBus,
    private readonly renderInvalidation?: RenderInvalidationService | null,
    private readonly graphics?: GraphicsBackend | null
  ) {
    this.eventBus = eventBus
  }

  /**
   * 注册渲染贡献者。
   *
   * @param contributor - 渲染贡献者
   */
  addRenderContributor(contributor: RenderContributor): void {
    if (this.disposed) return
    if (this.graphics) {
      assertGraphicsRequirements(contributor, this.graphics, {
        type: 'render-contributor',
        name: String(contributor.kind),
      })
    }
    const hadContributor = this.contributors.has(contributor)
    this.contributors.add(contributor)
    if (!hadContributor) {
      this.invalidatePipeline('render.pipeline.contributorAdded', contributor.kind)
    }
    log.debug(
      `[RenderPipeline] contributor added, kind=${contributor.kind}, phases=${contributor.phases.join(',')}`
    )
  }

  /**
   * 移除渲染贡献者。
   *
   * @param contributor - 渲染贡献者；不存在时忽略
   */
  removeRenderContributor(contributor: RenderContributor): void {
    const removed = this.contributors.delete(contributor)
    if (removed) {
      this.invalidatePipeline('render.pipeline.contributorRemoved', contributor.kind)
    }
    log.debug(`[RenderPipeline] contributor removed, kind=${contributor.kind}`)
  }

  /**
   * 注册后处理 pass。
   *
   * @param pass - 后处理 pass
   */
  addPostProcessPass(pass: PostProcessPass): void {
    if (this.disposed) return
    if (this.graphics) {
      assertGraphicsRequirements(pass, this.graphics, {
        type: 'postprocess',
        name: pass.name,
      })
    }
    const hadPass = this.postProcessPasses.has(pass)
    this.postProcessPasses.add(pass)
    if (!hadPass) {
      this.invalidatePipeline('render.pipeline.postProcessAdded', pass.name)
    }
    log.debug(`[RenderPipeline] postProcess added, name=${pass.name}`)
  }

  /**
   * 移除后处理 pass。
   *
   * @param pass - 后处理 pass；不存在时忽略
   */
  removePostProcessPass(pass: PostProcessPass): void {
    const removed = this.postProcessPasses.delete(pass)
    if (removed) {
      this.invalidatePipeline('render.pipeline.postProcessRemoved', pass.name)
    }
    log.debug(`[RenderPipeline] postProcess removed, name=${pass.name}`)
  }

  /**
   * 执行一帧渲染。
   *
   * @param frame - 视图帧上下文
   */
  /**
   * Executes one frame render.
   *
   * @param frame - View frame context.
   * @param options - Optional render target and viewport overrides.
   */
  execute(frame: ViewFrameContext, options?: RenderPipelineExecuteOptions): void {
    if (this.disposed) return

    const viewport = options?.viewportOverride ?? frame.viewport
    const graphics = frame.graphics
    const outputTargetRequested = options?.outputTarget !== undefined
    if (graphics.kind === 'webgpu' && (outputTargetRequested || options?.viewportOverride)) {
      throw new BackendMismatchError(
        'webgl2',
        graphics.kind,
        'RenderPipeline legacy outputTarget/viewportOverride'
      )
    }

    const entryState = graphics.captureState(this)
    // 区域采集期间，贡献者读到/绑定的 outputTarget 必须是 backend-owned 采集目标，
    // 否则显式绑定 outputTarget 的贡献者（如 WebGL 3DGS 合成、兼容提交）会把内容画到默认 surface。
    const outputTarget =
      graphics.kind === 'webgl2'
        ? (options?.outputTarget ?? resolveLegacyCaptureTarget(graphics))
        : null

    try {
      if (graphics.kind === 'webgl2') {
        const webglBackend = graphics as typeof graphics & {
          applyLegacyPipelineState?: (
            target: THREE.WebGLRenderTarget | null,
            rect: ViewFrameContext['viewport']
          ) => void
        }
        if (typeof webglBackend.applyLegacyPipelineState !== 'function') {
          throw new BackendMismatchError('webgl2', graphics.kind, 'WebGL2Backend pipeline state')
        }
        webglBackend.applyLegacyPipelineState(outputTarget, viewport)
      }

      for (const phase of PHASE_ORDER) {
        const phaseContributors = this.collectContributorsForPhase(phase)
        if (phaseContributors.length === 0) continue
        const mgr = getLoggerManager()
        const phaseStart = mgr.isPerfEnabled() ? mgr.now() : 0

        phaseContributors.sort((a, b) => (a.renderPriority ?? 0) - (b.renderPriority ?? 0))

        const renderContextBase = defineLegacyRendererAccessor(
          {
            graphics,
            viewId: frame.viewId,
            viewport,
            viewRect: frame.viewRect,
            // 离屏判断走 backend 的 neutralize 入口：WebGL2 由 legacy target 推断，
            // WebGPU 由 backend-owned capture 状态上报。
            rendersToOffscreenTarget:
              outputTarget !== null || (graphics.isOffscreenSubmit?.() ?? false),
          },
          graphics,
          () => frame.renderer
        )
        const renderContext = defineLegacyOutputTargetAccessor(
          renderContextBase,
          graphics,
          () => outputTarget
        ) satisfies PhaseRenderContext
        const viewBaseline = graphics.captureState(this)

        for (const contributor of phaseContributors) {
          graphics.restoreState(viewBaseline, this)
          try {
            contributor.render(frame, phase, renderContext)
          } catch (err) {
            log.error(
              `[RenderPipeline] contributor failed, kind=${contributor.kind}, phase=${phase}, reason=${
                err instanceof Error ? err.message : String(err)
              }`
            )
            this.eventBus.emit('engine.error', {
              error: err instanceof Error ? err : new Error(String(err)),
              context: `RenderPipeline: contributor "${contributor.kind}" in phase "${phase}" render failed`,
            })
          } finally {
            graphics.restoreState(viewBaseline, this)
          }
        }
        if (mgr.isPerfEnabled()) {
          const elapsed = mgr.now() - phaseStart
          if (elapsed >= RENDER_PHASE_SLOW_MS) {
            mgr.writeRecord({
              level: LogLevel.DEBUG,
              namespace: 'render:pipeline:perf',
              message: `slow phase ${phase} ${elapsed.toFixed(1)}ms, view=${frame.viewId}, frame=${frame.frameNumber}, contributors=${phaseContributors.length}`,
              timestamp: mgr.now(),
            })
          }
        }
      }
    } finally {
      graphics.restoreState(entryState, this)
    }
  }

  /**
   * 聚合渲染贡献者为当前视图声明的相机裁剪范围。
   *
   * @param context - 当前视图上下文
   * @returns 有效贡献范围的世界包围盒并集
   */
  collectCameraClipBounds(context: CameraClipBoundsContext): THREE.Box3 {
    const result = new THREE.Box3()
    if (this.disposed) {
      return result
    }

    for (const contributor of this.contributors) {
      if (!contributor.getCameraClipBounds) {
        continue
      }

      try {
        const bounds = contributor.getCameraClipBounds(context)
        if (!bounds || bounds.isEmpty() || !isFiniteBox(bounds)) {
          continue
        }
        result.union(bounds)
      } catch (err) {
        log.warn(
          `[RenderPipeline] camera clip bounds query failed, kind=${contributor.kind}, reason=${
            err instanceof Error ? err.message : String(err)
          }`
        )
      }
    }

    return result
  }

  /**
   * 响应尺寸变化；目前为占位，预留 render target 调整。
   *
   * @param _width - 新宽度，单位像素
   * @param _height - 新高度，单位像素
   */
  resize(_width: number, _height: number): void {
    // Reserved for future render target sizing.
  }

  collectRenderActivity(context: RenderActivityContext): RenderActivityReport {
    if (this.disposed) {
      return { needsNextFrame: false, reasons: [] }
    }

    const reasons: string[] = []
    const viewIds = new Set<string>()
    let hasGlobalActivity = false
    for (const contributor of Array.from(this.contributors)) {
      if (!contributor.getRenderActivity) {
        continue
      }

      try {
        const report = contributor.getRenderActivity(context)
        if (!report?.needsNextFrame) {
          continue
        }
        if (report.reasons?.length) {
          reasons.push(...report.reasons)
        } else {
          reasons.push(contributor.kind)
        }
        if (report.viewIds?.length) {
          for (const viewId of report.viewIds) {
            viewIds.add(viewId)
          }
        } else {
          hasGlobalActivity = true
        }
      } catch (err) {
        log.warn(
          `[RenderPipeline] activity query failed, kind=${contributor.kind}, reason=${
            err instanceof Error ? err.message : String(err)
          }`
        )
      }
    }

    return {
      needsNextFrame: reasons.length > 0,
      reasons,
      ...(!hasGlobalActivity && viewIds.size > 0 ? { viewIds: Array.from(viewIds) } : {}),
    }
  }

  /** 释放管线，清空贡献者与后处理 pass */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.contributors.clear()

    for (const pass of Array.from(this.postProcessPasses)) {
      try {
        pass.dispose()
      } catch (err) {
        this.eventBus.emit('engine.error', {
          error: err instanceof Error ? err : new Error(String(err)),
          context: `RenderPipeline: post-process pass "${pass.name}" dispose failed`,
        })
      }
    }
    this.postProcessPasses.clear()
  }

  private collectContributorsForPhase(phase: RenderPhase): RenderContributor[] {
    const result: RenderContributor[] = []
    for (const contributor of Array.from(this.contributors)) {
      if (contributor.phases.includes(phase)) {
        result.push(contributor)
      }
    }
    return result
  }

  private invalidatePipeline(reason: string, detail: string): void {
    this.renderInvalidation?.invalidate(reason, { detail })
  }
}
